// Android hardware/gesture back button.
//
// The default Capacitor behaviour on the AuthContext'd pages used to bubble
// `history.back()` past the Login page, which the AuthProvider interpreted as
// a sign-out. Users complained that pressing back a few times would silently
// log them out. We own the back stack here instead:
//   1. If an open shadcn Dialog / Sheet / Popover is on screen → close it.
//   2. Else if we're on a child route → `history.back()`.
//   3. Else (we're on the Home route "/") → double-tap-to-exit toast, then
//      quit the app on a second press within 2s. Never navigate to /login
//      implicitly.
import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const EXIT_HINT_MS = 2000;

export default function AndroidBackHandler() {
  const location = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    const cap = window.Capacitor;
    if (!cap?.isNativePlatform?.() || cap.getPlatform?.() !== "android") return;

    let listener;
    let lastExitAt = 0;

    const closeTopOverlay = () => {
      const sel = [
        '[data-state="open"][role="dialog"] [data-dismiss]',
        '[data-state="open"][role="dialog"] button[aria-label*="إغلاق" i]',
        '[data-state="open"][role="dialog"] button[aria-label*="close" i]',
      ].join(",");
      const btn = document.querySelector(sel);
      if (btn) { btn.click(); return true; }
      const anyOpenOverlay = document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][data-radix-popper-content-wrapper], [data-radix-drawer-content][data-state="open"]',
      );
      if (anyOpenOverlay) {
        document.dispatchEvent(new KeyboardEvent("keydown", { key: "Escape", bubbles: true }));
        return true;
      }
      return false;
    };

    const onBack = async ({ canGoBack }) => {
      if (closeTopOverlay()) return;
      const path = location.pathname;
      if (path !== "/" && path !== "/home" && canGoBack) { nav(-1); return; }
      const now = Date.now();
      if (now - lastExitAt < EXIT_HINT_MS) {
        try { const { App } = await import("@capacitor/app"); await App.exitApp(); }
        catch { /* ignore */ }
        return;
      }
      lastExitAt = now;
      toast.info("اضغط رجوع مرة أخرى للخروج من التطبيق", { duration: EXIT_HINT_MS });
    };

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        listener = await App.addListener("backButton", onBack);
      } catch { /* native plugin missing — nothing to do */ }
    })();

    return () => { try { listener?.remove?.(); } catch { /* ignore */ } };
  }, [location.pathname, nav]);

  return null;
}
